/*
 |--------------------------------------------------------------------------
 | Site header search bar module with suggestions
 |
 | Works with existing a-site-header open/close behavior.
 | Expected markup:
 | - .a-site-header__search
 | - .a-site-header__search-box
 | - .a-site-header__search-input-field
 | - .a-site-header__search-button
 |
 | Optional data attributes on .a-site-header__search:
 | - data-organization
 | - data-key
 | - data-searchhub
 | - data-search-action (fallback submit url, default: /zoeken?q=)
 | - data-mock-suggestions (JSON array of strings)
 |--------------------------------------------------------------------------
 */

const ASearchRootSelector = '[data-site-header-search]';
const ASearchInputSelector = '[data-site-header-search-input]';
const ASearchSubmitButtonSelector = '[data-site-header-search-submit]';
const ASearchClearButtonSelector = '[data-site-header-search-clear]';
const ASearchToggleButtonSelector = '[data-site-header-search-toggle]';
const ASearchOpenButtonSelector = '[data-site-header-search-open]';
const ASearchCloseButtonSelector = '[data-site-header-search-close]';
const ASearchWrapperSelector = '[data-site-header-search-wrapper]';

const ASearchSuggestionsClass = 'a-search-suggestions';
const ASearchSuggestionsVisibleClass = `${ASearchSuggestionsClass}--visible`;
const ASearchSuggestionsSelectedClass = `${ASearchSuggestionsClass}--item__selected`;
const ASearchSuggestionItemClass = `${ASearchSuggestionsClass}--item`;
const ASearchActiveClass = 'a-site-header__search--is-open';

const ASearchFetchSuggestionsDebounce = 200;
const ASearchSuggestionCount = 5;

class ASiteHeaderSearchBar {
  constructor(root) {
    this.root = root;
    this.wrapper = root.querySelector(ASearchWrapperSelector) || root;
    this.input = root.querySelector(ASearchInputSelector);
    this.button = root.querySelector(ASearchSubmitButtonSelector);
    this.clearButton = root.querySelector(ASearchClearButtonSelector);
    this.form = this.input ? this.input.closest('form') : null;

    this.selectedIndex = -1;
    this.items = [];
    this.abortController = null;
    this.requestTimer = null;
    this.listId =
      'a-site-header-suggestions-' + Math.random().toString(36).slice(2);

    this.organization = root.dataset.organization || '';
    this.apiKey = root.dataset.key || '';
    this.searchHub = root.dataset.searchhub || '';
    this.searchAction = root.dataset.searchAction || '/zoeken?q=';
    this.mockSuggestions = this.getMockSuggestions(
      root.dataset.mockSuggestions
    );

    this.coveoHistoryKey = '__coveo.analytics.history';
    this.coveoVisitorIdKey = 'visitorId';

    this.apiUrl = this.organization
      ? 'https://' +
        this.organization +
        '.org.coveo.com/rest/search/v2/querySuggest?organizationId=' +
        encodeURIComponent(this.organization)
      : '';
  }

  init() {
    if (!this.root || !this.input || !this.button) {
      return;
    }

    this.ensureSuggestionsElements();
    this.updateClearButtonVisibility();
    this.bindEvents();
  }

  ensureSuggestionsElements() {
    let popover = this.root.querySelector(
      '.a-popover.a-search-suggestions__popover'
    );
    if (!popover) {
      popover = document.createElement('div');
      popover.className =
        'a-popover a-search-suggestions__popover a-popover--mobile-anchored';
      this.wrapper.appendChild(popover);
    }

    this.popover = popover;

    let list = popover.querySelector('.' + ASearchSuggestionsClass);
    if (!list) {
      list = document.createElement('ul');
      list.className = ASearchSuggestionsClass;
      list.id = this.listId;
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', 'Zoeksuggesties');
      popover.appendChild(list);
    }

    this.list = list;

    let live = this.root.querySelector('.a-search-suggestions--live');
    if (!live) {
      live = document.createElement('div');
      live.className = 'a-search-suggestions--live sr-only';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      this.wrapper.appendChild(live);
    }

    this.liveRegion = live;

    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', this.list.id);
    this.input.setAttribute('aria-expanded', 'false');
  }

  bindEvents() {
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.onClear());
    }

    const toggleButton = document.querySelectorAll(ASearchToggleButtonSelector);

    if (toggleButton?.length) {
      toggleButton.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggleSearch();
        });
      });
    }

    const openButton = document.querySelectorAll(ASearchOpenButtonSelector);
    if (openButton?.length) {
      openButton.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.openSearch();
        });
      });
    }

    const closeButton = document.querySelectorAll(ASearchCloseButtonSelector);
    if (closeButton?.length) {
      closeButton.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.closeSearch();
        });
      });
    }

    this.button.addEventListener('click', (event) => {
      if (!this.form) {
        event.preventDefault();
        this.submitSearch();
      }
    });

    this.list.addEventListener('click', (event) => {
      const li = event.target.closest('li');
      if (!li) {
        return;
      }

      event.preventDefault();
      this.chooseSuggestion(li);
    });

    this.list.addEventListener('mouseover', (event) => {
      const li = event.target.closest('li');
      if (!li) {
        return;
      }

      this.selectedIndex = this.items.indexOf(li);
      this.applySelection(false);
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!this.root.contains(event.target)) {
          this.closeSearch();
        }
      },
      true
    );
  }

  toggleSearch() {
    if (this.root.classList.contains(ASearchActiveClass)) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    this.root.classList.add(ASearchActiveClass);
    this.input.focus();
  }

  closeSearch() {
    this.root.classList.remove(ASearchActiveClass);
    this.input.blur();
    this.hideSuggestions();
    this.updateClearButtonVisibility();
  }

  onInput() {
    this.selectedIndex = -1;
    this.updateClearButtonVisibility();

    const value = this.input.value.trim();
    if (value.length < 2) {
      this.hideSuggestions();
      return;
    }

    window.clearTimeout(this.requestTimer);
    this.requestTimer = window.setTimeout(() => {
      this.fetchSuggestions(value)
        .then((suggestions) => this.renderSuggestions(suggestions))
        .catch(() => this.hideSuggestions());
    }, ASearchFetchSuggestionsDebounce);
  }

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        if (!this.items.length) return;
        event.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          this.items.length - 1
        );
        this.applySelection(true);
        break;

      case 'ArrowUp':
        if (!this.items.length) return;
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.applySelection(true);
        break;

      case 'Enter':
        if (this.selectedIndex > -1 && this.items[this.selectedIndex]) {
          event.preventDefault();
          this.chooseSuggestion(this.items[this.selectedIndex]);
          break;
        }
        this.hideSuggestions();
        if (!this.form) {
          event.preventDefault();
          this.submitSearch();
        }
        break;

      case 'Escape':
        if (this.input.value.trim().length === 0) {
          this.closeSearch();
        } else {
          this.hideSuggestions();
        }
        break;
    }
  }

  applySelection(updateInput) {
    this.items.forEach((li, index) => {
      const isSelected = index === this.selectedIndex;
      li.classList.toggle(ASearchSuggestionsSelectedClass, isSelected);
      li.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    if (this.selectedIndex > -1 && this.items[this.selectedIndex]) {
      const selected = this.items[this.selectedIndex];
      this.input.setAttribute('aria-activedescendant', selected.id);

      if (updateInput) {
        this.input.value = selected.dataset.value || selected.textContent || '';
      }
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  chooseSuggestion(li) {
    this.input.value = li.dataset.value || li.textContent || '';
    this.hideSuggestions();
    this.submitSearch();
  }

  submitSearch() {
    const value = this.input.value.trim();
    if (!value) {
      return;
    }

    if (this.form) {
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
      return;
    }

    const url = this.buildSearchUrl(value);
    window.location.href = url;
  }

  buildSearchUrl(query) {
    const encoded = encodeURIComponent(query);

    if (this.searchAction.indexOf('{q}') > -1) {
      return this.searchAction.replace('{q}', encoded);
    }

    if (this.searchAction.indexOf('?') > -1) {
      const url = new URL(this.searchAction, window.location.href);
      url.searchParams.set('q', query);
      return url.pathname + url.search + url.hash;
    }

    return this.searchAction + encoded;
  }

  hideSuggestions() {
    this.items = [];
    this.selectedIndex = -1;

    this.list.innerHTML = '';
    this.popover.removeAttribute('open');
    this.root.classList.remove(ASearchSuggestionsVisibleClass);

    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');

    if (this.liveRegion) {
      this.liveRegion.textContent = '';
    }
  }

  updateClearButtonVisibility() {
    if (!this.clearButton) {
      return;
    }

    const hasContent = this.input.value.trim().length > 0;
    this.clearButton.hidden = !hasContent;
  }

  onClear() {
    this.input.value = '';
    this.hideSuggestions();
    this.updateClearButtonVisibility();
    this.input.focus();
  }

  renderSuggestions(suggestions) {
    if (!Array.isArray(suggestions) || suggestions.length === 0) {
      this.hideSuggestions();
      return;
    }

    this.list.innerHTML = '';
    this.items = [];

    suggestions.forEach((item, index) => {
      const li = document.createElement('li');
      li.id = this.listId + '-option-' + index;
      li.className = ASearchSuggestionItemClass;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');

      const value = item.value || '';
      const highlighted = item.highlighted || this.escapeHtml(value);

      li.dataset.value = value;
      li.innerHTML = highlighted;

      this.list.appendChild(li);
      this.items.push(li);
    });

    this.selectedIndex = -1;
    this.popover.setAttribute('open', '');
    this.root.classList.add(ASearchSuggestionsVisibleClass);
    this.input.setAttribute('aria-expanded', 'true');

    if (this.liveRegion) {
      this.liveRegion.textContent =
        this.items.length + ' zoeksuggesties beschikbaar';
    }
  }

  async fetchSuggestions(inputValue) {
    if (this.mockSuggestions.length > 0) {
      return this.getMockSuggestionsForQuery();
    }

    if (!this.apiUrl || !this.apiKey || !this.searchHub) {
      return [];
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    let data;
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: 'Bearer ' + this.apiKey,
        },
        signal: this.abortController.signal,
        body: this.getSuggestionParameters(inputValue),
      });

      if (!response.ok) {
        return [];
      }

      data = await response.json();
      return data;
    } catch {
      return [];
    }
  }

  getSuggestionParameters(inputValue) {
    const actionsHistory = localStorage.getItem(this.coveoHistoryKey);
    const visitorId = localStorage.getItem(this.coveoVisitorIdKey);

    const action = this.form && this.form.action ? this.form.action : '';
    const hashIndex = action.indexOf('#');
    const hashPart = hashIndex > -1 ? action.slice(hashIndex + 1) : '';
    const tab = new URLSearchParams(hashPart).get('tab');

    const params = new URLSearchParams();
    params.append('locale', 'nl');
    params.append('count', String(ASearchSuggestionCount));
    params.append('q', inputValue);
    params.append('enableWordCompletion', 'true');
    params.append('searchHub', this.searchHub);

    if (tab) {
      params.append('tab', tab);
    }

    if (actionsHistory) {
      params.append('actionsHistory', actionsHistory);
    }

    if (visitorId) {
      params.append('visitorId', visitorId);
    }

    return params;
  }

  // Mocking suggestions for testing purposes
  getMockSuggestions(rawValue) {
    if (!rawValue) {
      return [];
    }

    const trimmed = rawValue.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((value) => String(value).trim())
        .filter((value) => value.length > 0);
    } catch {
      return [];
    }
  }

  getMockSuggestionsForQuery() {
    return this.mockSuggestions.map((value) => {
      return {
        value,
        highlighted: value,
      };
    });
  }
}

function createSearchBarInstance(root) {
  if (!root || root.searchBarClass) {
    return;
  }

  root.searchBarClass = new ASiteHeaderSearchBar(root);
  root.searchBarClass.init();
}

document.querySelectorAll(ASearchRootSelector).forEach(createSearchBarInstance);

const searchBarObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (!node || node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      if (node.matches && node.matches(ASearchRootSelector)) {
        createSearchBarInstance(node);
      }

      if (node.querySelectorAll) {
        node
          .querySelectorAll(ASearchRootSelector)
          .forEach(createSearchBarInstance);
      }
    });
  });
});

searchBarObserver.observe(document, {
  attributes: false,
  childList: true,
  characterData: false,
  subtree: true,
});
